/**
 * The grader (spec 53 section 4.4): did the person do the module's graded
 * steps themselves?
 *
 * A pure function, and one of two copies: api/app/Services/Training/grade.js
 * is the other, and the server's answer is the one that counts. Both are
 * held to the same cases in api/app/Services/Training/__fixtures__/grade.json,
 * so change the fixture, then both copies, never one alone.
 *
 *   grade(module, stepLog, showMe?) -> { passed, steps: [{ id, kind, mode, counts }],
 *                                        assisted, missing: [{ id, title, reason }] }
 *
 * `module` is the registry entry; only its `graded: { find, enter, save }`
 * (step ids by kind; an entry may be a group of ids, any one of which
 * counts) is read. `stepLog` is what TourHost recorded:
 * [{ id, mode: 'self'|'shown'|'read'|'skipped'|null, refusals, title? }].
 *
 *  - `self` always counts. Whether `shown` (Show me, "Do this step for me")
 *    counts is the Show me policy's (spec 53 section 15.3): `showMe` is
 *    `{ [stepId]: boolean }`, Ops' non-default rows (showMePolicy.js). A step
 *    it does not name keeps the default of section 4.3: find counts `shown`
 *    (the element appeared, whoever opened it), enter and save count only
 *    `self`. An entry on any id of a group decides for the group (the
 *    group's first id with an entry, if two disagree). Omitted, every step
 *    keeps the default. A shown step counts as assisted either way.
 *  - A skipped graded step fails. Refusals are not failures; they are logged.
 *  - A module with nothing graded never passes (missing: 'not-ready').
 *  - An id logged twice (Back, then the step again) keeps its best mode:
 *    once the person has done it themselves, it is theirs.
 */
const MODES = ['self', 'shown', 'read', 'skipped'];
const RANK = { self: 4, shown: 3, read: 2, skipped: 1 };
const KINDS = ['find', 'enter', 'save'];
const TITLE_MAX = 80;
const NOT_READY = "Practice for this module isn't ready yet";

function rank(mode) {
  return RANK[mode] || 0;
}

/** One entry per id: best mode, refusals added up, the first title given. */
function collapse(stepLog) {
  const byId = new Map();
  for (const entry of Array.isArray(stepLog) ? stepLog : []) {
    if (!entry || typeof entry.id !== 'string') continue;
    const mode = MODES.includes(entry.mode) ? entry.mode : null;
    const refusals = Number.isInteger(entry.refusals) && entry.refusals > 0 ? entry.refusals : 0;
    const title =
      typeof entry.title === 'string' && entry.title.trim()
        ? entry.title.trim().slice(0, TITLE_MAX)
        : null;
    const seen = byId.get(entry.id);
    if (!seen) {
      byId.set(entry.id, { mode, refusals, title });
      continue;
    }
    if (rank(mode) > rank(seen.mode)) seen.mode = mode;
    seen.refusals += refusals;
    if (!seen.title) seen.title = title;
  }
  return byId;
}

/**
 * Does Show me count for this group? The policy's word on the first of its
 * ids the policy names (true or false only), else the default: find yes,
 * enter and save no.
 */
function shownCounts(group, showMe) {
  if (showMe && typeof showMe === 'object') {
    for (const id of group.ids) {
      if (Object.prototype.hasOwnProperty.call(showMe, id) && typeof showMe[id] === 'boolean') {
        return showMe[id];
      }
    }
  }
  return group.kind === 'find';
}

function counts(mode, shown) {
  if (mode === 'self') return true;
  return mode === 'shown' && shown;
}

function reasonFor(mode) {
  if (mode === 'shown') return 'shown';
  if (mode === 'skipped') return 'skipped';
  return 'not-done';
}

export function grade(module, stepLog, showMe) {
  const graded = (module && module.graded) || {};
  // Each graded entry is a step id, or a group of ids of which any one counts:
  // a walkthrough that shows one of two variants of a step (the named step, or
  // its `-any` stand-in for a fleet with none of that kind) logs only the one
  // it showed, so requiring both would fail every run.
  const groupOf = new Map();
  const groups = [];
  for (const kind of KINDS) {
    for (const item of Array.isArray(graded[kind]) ? graded[kind] : []) {
      const ids = (Array.isArray(item) ? item : [item]).filter(
        id => typeof id === 'string' && !groupOf.has(id)
      );
      if (!ids.length) continue;
      const group = { ids, kind };
      for (const id of ids) groupOf.set(id, group);
      groups.push(group);
    }
  }

  // A module with nothing graded is not ready to be passed: with no steps to
  // check, any log at all (an empty one included) would pass it, and staging
  // alone makes some modules' evidence (spec 53 review, 2026-10-27, F2).
  if (!groups.length) {
    return {
      passed: false,
      steps: [],
      assisted: 0,
      missing: [{ id: 'module', title: NOT_READY, reason: 'not-ready' }],
    };
  }

  const log = collapse(stepLog);
  // The order the person met the steps in, then any they never reached.
  const met = [];
  for (const id of log.keys()) {
    const group = groupOf.get(id);
    if (group && !met.includes(group)) met.push(group);
  }
  const ordered = met.concat(groups.filter(group => !met.includes(group)));

  const steps = [];
  const missing = [];
  let assisted = 0;
  for (const group of ordered) {
    const { kind } = group;
    // Of the group's ids the person reached, the one done best stands for it;
    // none reached, and it is missing under its first id.
    let id = group.ids[0];
    for (const candidate of group.ids) {
      if (
        log.has(candidate) &&
        (!log.has(id) || rank(log.get(candidate).mode) > rank(log.get(id).mode))
      ) {
        id = candidate;
      }
    }
    const entry = log.get(id) || null;
    const mode = entry ? entry.mode : null;
    const ok = counts(mode, shownCounts(group, showMe));
    if (mode === 'shown') assisted += 1;
    steps.push({ id, kind, mode, counts: ok });
    if (!ok) missing.push({ id, title: (entry && entry.title) || id, reason: reasonFor(mode) });
  }

  return { passed: missing.length === 0, steps, assisted, missing };
}
